import React, { useRef } from "react";
import { FormikProps } from "formik";
import Button from "../Button";

interface PropTypes {
  step2Form: FormikProps<{ otp: string }>;
  isResendDisabled: boolean;
  onResend: () => void;
  countDown: string;
  resendAttempts: number;
}

const OTPForm = ({
  step2Form,
  isResendDisabled,
  onResend,
  countDown,
  resendAttempts,
}: PropTypes) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const otpArray = step2Form.values.otp.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otpArray];
    newOtp[index] = value;

    step2Form.setFieldValue("otp", newOtp.join(""));

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pasteData)) return;

    step2Form.setFieldValue("otp", pasteData);
  };

  return (
    <form
      onSubmit={step2Form.handleSubmit}
      className="bg-white flex flex-col gap-5 p-6 lg:w-[380px] rounded-2xl mx-4 lg:mx-0 shadow-xl"
    >
      <div className="text-center space-y-1">
        <h2 className="text-xl font-semibold text-primary">Verify OTP</h2>
        <p className="text-gray-500 text-sm">
          Enter the 6-digit code sent to your email
        </p>
      </div>

      {/* OTP Inputs */}
      <div className="flex justify-between gap-2" onPaste={handlePaste}>
        {otpArray.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit || ""}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        ))}
      </div>

      {/* Error */}
      {step2Form.touched.otp && step2Form.errors.otp && (
        <p className="text-red-500 text-sm text-center">
          {step2Form.errors.otp}
        </p>
      )}

      {/* Resend */}
      <div className="flex justify-end">
        {resendAttempts < 2 ? (
          <Button
            onClick={onResend}
            disabled={isResendDisabled}
            type="button"
            variant="outline"
            className="text-primary !bg-transparent border-none text-sm"
          >
            {isResendDisabled
              ? `Resend in ${countDown}`
              : "Resend OTP"}
          </Button>
        ) : (
          <p className="text-xs text-gray-500">
            Maximum resend attempts reached.
          </p>
        )}
      </div>

      {/* Submit */}
      <Button
        isLoading={step2Form.isSubmitting}
        type="submit"
        className="py-2"
      >
        Verify OTP
      </Button>
    </form>
  );
};

export default OTPForm;